// The recordings the app fetched for itself, kept on the device's own filesystem.
//
// The build ships the word clips and not the 139 MB of sentences (docs/audio-on-the-phone.md).
// This is the other half of that decision: ask for them afterwards, in deck order, a few at a
// time, and never make anybody wait.
//
// NOT INDEXEDB. Eleven thousand small recordings in an object store is workable and wrong — media
// belongs in files, where the browser can stream it and where the space it takes is the app's
// storage rather than a blob nobody can account for. The origin private file system is exactly
// that: real files in the app's own container, the same place the progress database already lives.
//
// WHAT MAKES IT SAFE TO RESUME. Clips are fetched strictly in plan order, so "what do I have" is
// one number — how far down the plan we got — plus the handful that failed. That state lives in
// the same directory as the files it describes, so deleting the audio resets the bookkeeping with
// it and the two cannot disagree.

import { clipPath, type FetchProgress } from '../src/shared/clip-plan.js';

const DIR = 'audio';
const STATE = '.state.json';

/** Four at a time. Enough to keep a phone's connection busy, few enough to stay out of the way. */
const CONCURRENCY = 4;

/** Write the bookmark every this many clips, rather than after each one. */
const BOOKMARK_EVERY = 25;

interface State {
  version: 1;
  /** How far down the plan we have walked. */
  cursor: number;
  /** Clips actually written to this device. NOT the same as the cursor — see the note on run(). */
  have: number;
  /** Clips the server answered with a refusal. Written off; a gap that will not close by waiting. */
  missed: string[];
  /** Bytes actually written. Counted, not estimated. */
  bytes: number;
}

const blank = (): State => ({ version: 1, cursor: 0, have: 0, missed: [], bytes: 0 });

/** Why the last run stopped, when it was not because it finished or was asked to. */
export type Trouble = 'unreachable' | null;

let root: FileSystemDirectoryHandle | null = null;
let state: State = blank();
let missed = new Set<string>();
/** clip -> its position in the plan, so "do I have this?" is a lookup rather than a disk call. */
let position = new Map<string, number>();
let plan: string[] = [];
/** Where the clips live on the server, from the manifest. See clipPath. */
let prefix = 'audio/';
let running = false;
let stopping = false;
/**
 * Bumped whenever the files are thrown away.
 *
 * A download in flight is a batch of requests already sent. Deleting while they are out means
 * their results land on bookkeeping that no longer describes anything — the cursor would step
 * forward over files that had just been removed, and the app would claim recordings it does not
 * have. The loop carries the generation it started in and drops everything if it changed.
 */
let generation = 0;
/** The run in progress, so a delete can wait for it rather than race it. */
let inFlight: Promise<void> | null = null;
/** Everything that wants repainting when the download moves. The Settings row, today. */
const watchers = new Set<() => void>();
const onChange = (): void => {
  for (const w of watchers) w();
};

/** Repaint me when something changes. Returns nothing to unsubscribe: the watchers live as long
 *  as the page does, and a settings panel that stops being repainted is the bug this prevents. */
export function watch(paint: () => void): void {
  watchers.add(paint);
}

/** Whether this build can keep files at all. False in anything too old, and then nothing happens. */
export function supported(): boolean {
  return typeof navigator !== 'undefined' && !!navigator.storage?.getDirectory;
}

async function dir(): Promise<FileSystemDirectoryHandle | null> {
  if (root) return root;
  if (!supported()) return null;
  try {
    root = await (await navigator.storage.getDirectory()).getDirectoryHandle(DIR, { create: true });
  } catch {
    root = null;
  }
  return root;
}

/**
 * The extension the clips are in, from the manifest rather than assumed.
 *
 * Nothing in this file knows what language it is fetching. That matters more than it looks: the
 * plan is built from whatever decks the build carries, a clip is named after the text it speaks,
 * and the format and the origin are both things the manifest states. A build teaching English to
 * Chinese speakers would change the data and the manifest and nothing here.
 */
let format = 'mp3';

/** A clip's file, nested the way its name is: `s/9962513` lives in an `s` folder. */
async function fileHandle(
  clip: string,
  create: boolean,
): Promise<FileSystemFileHandle | null> {
  const base = await dir();
  if (!base) return null;
  const parts = clip.split('/');
  const name = `${parts.pop() as string}.${format}`;
  let here = base;
  try {
    for (const part of parts) here = await here.getDirectoryHandle(part, { create });
    return await here.getFileHandle(name, { create });
  } catch {
    return null;
  }
}

/**
 * What is already here, read back as a state this version understands.
 *
 * NORMALISED, NOT TRUSTED. A file written by an older build is missing whatever was added since —
 * `have` was, and the result was a settings panel that threw on `undefined.toLocaleString()`
 * before it could open, which looks like a dead button rather than a stale file. Every field gets
 * a defined value here, so nothing downstream has to wonder.
 *
 * `have` is derived where it is absent: everything walked, less what was refused. That is exactly
 * what the older build meant by its cursor.
 */
async function readState(): Promise<State> {
  const base = await dir();
  if (!base) return blank();
  try {
    const handle = await base.getFileHandle(STATE);
    const parsed = JSON.parse(await (await handle.getFile()).text()) as Partial<State>;
    if (parsed.version !== 1) return blank();
    const cursor = Number.isFinite(parsed.cursor) ? (parsed.cursor as number) : 0;
    const missedList = Array.isArray(parsed.missed) ? parsed.missed : [];
    return {
      version: 1,
      cursor,
      have: Number.isFinite(parsed.have)
        ? (parsed.have as number)
        : Math.max(0, cursor - missedList.length),
      missed: missedList,
      bytes: Number.isFinite(parsed.bytes) ? (parsed.bytes as number) : 0,
    };
  } catch {
    return blank();
  }
}

async function writeState(): Promise<void> {
  const base = await dir();
  if (!base) return;
  try {
    const handle = await base.getFileHandle(STATE, { create: true });
    const w = await handle.createWritable();
    await w.write(JSON.stringify(state));
    await w.close();
  } catch {
    /* out of space, or no permission — the fetch itself will fail next and say so */
  }
}

/**
 * Load what is already here.
 *
 * Called once at boot with the plan. Until it resolves `have()` says no to everything, which is
 * the right answer: the app falls back to the system voice, as it does for any clip it lacks.
 */
export async function load(fetchPlan: string[], clipFormat: string, clipPrefix = 'audio/'): Promise<void> {
  plan = fetchPlan;
  format = clipFormat;
  prefix = clipPrefix;
  position = new Map(plan.map((clip, i) => [clip, i]));
  state = await readState();
  missed = new Set(state.missed);
  onChange();
}

/** Is this clip on the device? SYNCHRONOUS, because the answer decides what a tap does. */
export function have(clip: string): boolean {
  const at = position.get(clip);
  return at !== undefined && at < state.cursor && !missed.has(clip);
}

/**
 * A URL the audio element can play, or null.
 *
 * The caller is expected to have this ready BEFORE the tap — see the note on preloading in
 * web/app.ts. Reading a file is asynchronous and a tap is not.
 */
export async function url(clip: string): Promise<string | null> {
  if (!have(clip)) return null;
  const handle = await fileHandle(clip, false);
  if (!handle) return null;
  try {
    return URL.createObjectURL(await handle.getFile());
  } catch {
    return null;
  }
}

/**
 * How much is actually here.
 *
 * `have`, NOT the cursor. They came apart the first time this ran against a server that refused
 * the requests: every clip failed, the cursor marched on regardless, and the bar filled while the
 * device stayed empty. A progress bar that counts attempts is a progress bar that lies.
 */
export function progress(totalBytes: number): FetchProgress {
  return { done: state.have, total: plan.length, bytes: state.bytes, totalBytes };
}

let trouble: Trouble = null;

/** What went wrong, if anything, so the learner is told rather than left watching nothing. */
export function lastTrouble(): Trouble {
  return trouble;
}

/** True while the download is actually moving. */
export function fetching(): boolean {
  return running;
}

/** A clip the server refused: it will not arrive by waiting, so the plan steps over it. */
class Refused extends Error {}

async function fetchOne(origin: string, clip: string, mine: number): Promise<number> {
  // Already here? Then there is nothing to ask for. THE DISK IS THE TRUTH and the cursor is only
  // a hint about where to look next — which is what makes a second pass cheap, and what lets a
  // learner whose first attempt half-failed simply press the button again.
  const existing = await fileHandle(clip, false);
  if (existing) {
    try {
      return (await existing.getFile()).size;
    } catch {
      /* unreadable — fall through and fetch it again */
    }
  }
  // A network failure and a refusal are different facts and must not be treated alike. One is
  // "come back later", the other is "this one is not there". Conflating them is how a download
  // walks the whole plan writing nothing off a server it never reached.
  let res: Response;
  try {
    res = await fetch(`${origin.replace(/\/$/u, '')}/${clipPath(clip, format, prefix)}`);
  } catch (err) {
    throw new Error(`unreachable: ${(err as Error).message}`);
  }
  if (!res.ok) throw new Refused(`${res.status}`);
  const bytes = await res.arrayBuffer();
  // Checked HERE, not only around the batch: between asking for a clip and writing it the learner
  // may have deleted the lot, and a write that lands afterwards is a file nothing will ever use
  // and nothing will ever tidy up.
  if (generation !== mine) throw new Error('deleted while in flight');
  const handle = await fileHandle(clip, true);
  if (!handle) throw new Error('nowhere to put it');
  const w = await handle.createWritable();
  await w.write(bytes);
  await w.close();
  return bytes.byteLength;
}

/**
 * Fetch what is left, from where the manifest says, until it is done or told to stop.
 *
 * NEVER A GATE. This runs beside a session that is already working; every failure is absorbed and
 * retried on the next run rather than raised. Going offline pauses it — the app is not offline,
 * only this is, and a learner mid-card should not be told about it.
 */
export async function start(origin: string): Promise<void> {
  if (running || !plan.length) return;
  if (!(await dir())) return;
  // Walked the whole plan and still short? Then go round again.
  //
  // Without this the button does nothing, which is how an early build left a device: every clip
  // written off after a server that could not be reached, the cursor at the end, and no way back
  // except deleting. Anything already on disk is skipped in a moment, so a second pass costs
  // almost nothing and is the obvious thing for pressing Download to mean.
  if (state.cursor >= plan.length && state.have < plan.length) {
    state.cursor = 0;
    missed.clear();
    state.missed = [];
    state.have = 0;
    state.bytes = 0;
  }
  running = true;
  stopping = false;
  onChange();
  inFlight = run(origin);
  await inFlight;
  inFlight = null;
}

async function run(origin: string): Promise<void> {
  const mine = generation;
  let sinceBookmark = 0;
  trouble = null;
  try {
    while (!stopping && generation === mine && state.cursor < plan.length) {
      if (typeof navigator !== 'undefined' && navigator.onLine === false) break;
      const batch = plan.slice(state.cursor, state.cursor + CONCURRENCY);
      const results = await Promise.all(
        batch.map(async (clip) => {
          try {
            return { clip, bytes: await fetchOne(origin, clip, mine), refused: false };
          } catch (err) {
            return { clip, bytes: -1, refused: err instanceof Refused };
          }
        }),
      );
      // Everything this batch learned is void if the files were deleted while it was in the air.
      if (generation !== mine) break;

      // A batch where nothing could even be REACHED means the server is not there. Stopping is the
      // only honest response: walking on would step the plan past ten thousand clips that were
      // never asked for properly, and a later run would think it had them.
      if (results.every((r) => r.bytes < 0 && !r.refused)) {
        trouble = 'unreachable';
        break;
      }

      for (const r of results) {
        if (r.bytes < 0) {
          // Refused is written off — it will not arrive by waiting. Anything else is left for the
          // next run, which is what the cursor NOT counting it achieves.
          if (r.refused) missed.add(r.clip);
        } else {
          state.have++;
          state.bytes += r.bytes;
        }
      }
      state.cursor += batch.length;
      state.missed = [...missed];
      sinceBookmark += batch.length;
      if (sinceBookmark >= BOOKMARK_EVERY) {
        sinceBookmark = 0;
        await writeState();
      }
      onChange();
    }
  } finally {
    running = false;
    if (generation === mine) await writeState();
    onChange();
  }
}

export function stop(): void {
  stopping = true;
}

/** Throw it all away — the files and the bookkeeping together, so they cannot disagree. */
export async function clear(): Promise<void> {
  stop();
  generation++;
  // Wait for whatever is in the air to land before sweeping, or the sweep runs first and the
  // stragglers recreate the directories behind it — 202 orphan files, measured.
  await inFlight?.catch(() => undefined);
  const opfs = await navigator.storage.getDirectory().catch(() => null);
  if (opfs) await opfs.removeEntry(DIR, { recursive: true }).catch(() => undefined);
  root = null;
  state = blank();
  missed = new Set();
  onChange();
}
